import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { RefreshCw, ChevronDown, ChevronUp, FileText, PlayCircle, Search, Plus } from 'lucide-react';
import api from '../api';

const BillingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [expandedWidgets, setExpandedWidgets] = useState({
    rapDue: true,
    noaDue: true
  });

  const [noaData, setNoaData] = useState([
    { patient: 'GUERRERO, LISA K.', pan: 30, admitDate: '05/15/2023', noaStart: '05/15/2023' },
    { patient: 'BAINTON, GLADYS', pan: 28, admitDate: '07/18/2023', noaStart: '07/18/2023' },
    { patient: 'LEE, DOROTHY', pan: 26, admitDate: '07/21/2023', noaStart: '07/21/2023' },
    { patient: 'LOCOCO, STEVE T.', pan: 36, admitDate: '09/23/2023', noaStart: '09/23/2023' },
  ]);

  useEffect(() => {
    setTimeout(() => setLoading(false), 800);
  }, []);

  const toggleWidget = (widget) => {
    setExpandedWidgets(prev => ({
      ...prev,
      [widget]: !prev[widget]
    }));
  };

  const WidgetHeader = ({ title, widgetKey, color = 'bg-[#3498db]' }) => (
    <div 
      className={`flex items-center justify-between p-3 ${color} text-white cursor-pointer`}
      onClick={() => toggleWidget(widgetKey)}
    >
      <h3 className="font-semibold">{title}</h3>
      {expandedWidgets[widgetKey] ? (
        <ChevronUp className="w-5 h-5" />
      ) : (
        <ChevronDown className="w-5 h-5" />
      )}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#3498db]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="billing-dashboard">
        {/* Quick Links */}
        <div className="grid grid-cols-4 gap-4">
          <button className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Announcements</span>
          </button>
          <button className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Live Training</span>
          </button>
          <button className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Training Videos</span>
          </button>
          <button className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-yellow-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Add Features/Licenses</span>
          </button>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* RAP Due */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <WidgetHeader title="RAP Due" widgetKey="rapDue" color="bg-[#3498db]" />
            {expandedWidgets.rapDue && (
              <div className="p-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️</strong> This widget will display all billable RAP claims. Claims older than 30 days would be rejected and so are not displayed in this view.
                  </p>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm">Search:</span>
                  <input type="text" className="border rounded px-2 py-1 text-sm flex-1" />
                  <select className="border rounded px-2 py-1 text-sm">
                    <option>Show 10 entries</option>
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">RAP Patient</th>
                        <th className="px-3 py-2 text-left">PAN</th>
                        <th className="px-3 py-2 text-left">Episode</th>
                        <th className="px-3 py-2 text-left">Billing Period</th>
                        <th className="px-3 py-2 text-left text-[#e74c3c]">Days until R</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                          No data available in table
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                  <span>Showing 0 to 0 of 0 entries</span>
                  <div className="flex gap-1">
                    <button className="px-2 py-1 border rounded">First</button>
                    <button className="px-2 py-1 border rounded">Previous</button>
                    <button className="px-2 py-1 border rounded">Next</button>
                    <button className="px-2 py-1 border rounded">Last</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* NOA Due */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <WidgetHeader title="NOA Due" widgetKey="noaDue" color="bg-[#f39c12]" />
            {expandedWidgets.noaDue && (
              <div className="p-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>ℹ️</strong> This widget will display all pending Notices of Admission. EOE submitted without notice of admission will be rejected. Notice of Admission is required for any claim period to be billed, and will remain visible until NOA is created.
                  </p>
                </div>
                <div className="mb-4">
                  <button className="px-4 py-2 bg-[#27ae60] text-white rounded hover:bg-[#2ecc71]">
                    Generate NOA(s) for Selected
                  </button>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm">Search:</span>
                  <input type="text" className="border rounded px-2 py-1 text-sm flex-1" />
                  <select className="border rounded px-2 py-1 text-sm">
                    <option>Show 10 entries</option>
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">
                          <input type="checkbox" className="rounded" />
                        </th>
                        <th className="px-3 py-2 text-left">Patient</th>
                        <th className="px-3 py-2 text-left">PAN</th>
                        <th className="px-3 py-2 text-left">Admit Date</th>
                        <th className="px-3 py-2 text-left">NOA Start</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {noaData.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input type="checkbox" className="rounded" />
                          </td>
                          <td className="px-3 py-2">{row.patient}</td>
                          <td className="px-3 py-2">{row.pan}</td>
                          <td className="px-3 py-2">{row.admitDate}</td>
                          <td className="px-3 py-2">{row.noaStart}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                  <span>Showing 1 to 4 of 4 entries</span>
                  <div className="flex gap-1">
                    <button className="px-2 py-1 border rounded">First</button>
                    <button className="px-2 py-1 border rounded">Previous</button>
                    <button className="px-2 py-1 border rounded bg-[#3498db] text-white">1</button>
                    <button className="px-2 py-1 border rounded">Next</button>
                    <button className="px-2 py-1 border rounded">Last</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 py-4">
          © 2026 Ivory Healthcare Systems, LLC. Version 8.61.4.19
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BillingDashboard;
